import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Truck, Package, MapPin, Settings, FlaskConical, Webhook,
  Download, RefreshCw, ChevronRight, ArrowLeft, Loader2,
  CheckCircle2, XCircle, AlertCircle, Activity, ShieldCheck, KeyRound, Eye, EyeOff, X, Ban,
} from "lucide-react";

// =============================================================================
// P1.4a — Tenant Shipping Admin UI
// -----------------------------------------------------------------------------
// Consumes ONLY existing backend APIs:
//   GET    /api/shipping/config
//   PUT    /api/shipping/config
//   POST   /api/shipping/config/profiles
//   DELETE /api/shipping/config/profiles/{id}
//   GET    /api/shipping/providers
//   PUT    /api/shipping/config/providers/{name}
//   POST   /api/shipping/rates
//   POST   /api/shipping/labels
//   GET    /api/shipping/labels/{id}/download (binary)
//   GET    /api/shipping/shipments
//   GET    /api/shipping/shipments/{id}
//   GET    /api/shipping/shipments/{id}/events
//   POST   /api/shipping/shipments/{id}/poll
//   GET    /api/shipping/track/{tracking_number}
//
// The page NEVER asks the user for plaintext credentials and NEVER renders
// the `*_ref` UUIDs that map config slots to encrypted secret rows.
// =============================================================================

const STRATEGY_OPTIONS = [
  { value: "manual",         label: "Manual (caller picks provider)" },
  { value: "tenant_default", label: "Tenant default provider" },
  { value: "cheapest",       label: "Cheapest available" },
  { value: "fastest",        label: "Fastest available" },
  { value: "best_value",     label: "Best value (60% price + 40% speed)" },
];

const STATUS_BADGE = {
  pending:          "bg-slate-100 text-slate-700 border-slate-200",
  label_created:    "bg-sky-50 text-sky-700 border-sky-200",
  in_transit:       "bg-amber-50 text-amber-700 border-amber-200",
  out_for_delivery: "bg-amber-100 text-amber-800 border-amber-300",
  delivered:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  exception:        "bg-rose-50 text-rose-700 border-rose-200",
  returned:         "bg-rose-50 text-rose-700 border-rose-200",
  cancelled:        "bg-slate-100 text-slate-500 border-slate-200",
  voided:           "bg-slate-100 text-slate-500 border-slate-200",
};

function StatusPill({ status }) {
  const klass = STATUS_BADGE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${klass}`}
    >
      {status?.replace(/_/g, " ")}
    </span>
  );
}

// Feb 2026 production cutover — shipment-level mode badge.
//   * `live`     → red LIVE pill (real carrier charge)
//   * `sandbox`  → subtle grey pill (test data)
//   * `mock`     → subtle grey pill
//   * `null` / undefined → yellow "LEGACY" pill because the stored
//     row predates the sprint. Per audit rule we do NOT silently
//     represent legacy rows as sandbox — they read as
//     "MODE NOT RECORDED".
function ShipmentModeBadge({ mode }) {
  if (mode == null) {
    return (
      <span
        data-testid="shipment-mode-badge-legacy"
        title="Shipment predates the Feb 2026 audit — original mode not stored"
        className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full border border-amber-300 bg-amber-50 text-amber-800 uppercase tracking-wide"
      >
        legacy
      </span>
    );
  }
  if (mode === "live") {
    return (
      <span
        data-testid="shipment-mode-badge-live"
        className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full border border-red-400 bg-red-50 text-red-700 uppercase tracking-wide"
      >
        live
      </span>
    );
  }
  return (
    <span
      data-testid={`shipment-mode-badge-${mode}`}
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full border border-slate-300 bg-slate-50 text-slate-600 uppercase tracking-wide"
    >
      {mode}
    </span>
  );
}

// Provider Activation Sprint (Feb 2026) — capability badges rendered
// on each carrier card. Pulls live capability metadata from
// `GET /api/shipping/providers` (server-side), so adding a future
// capability is purely additive: bump the backend mapping and the UI
// reflects it on next render.
const CAPABILITY_LABELS = {
  rate:               "Rates",
  create_label:       "Labels",
  track:              "Tracking",
  void_label:         "Void",
  address_validation: "Address Validation",
  validate_credentials: null,                // internal, don't render
};

function CarrierCapabilityBadges({ meta }) {
  if (!meta) return null;
  const caps = Array.isArray(meta.capabilities) ? meta.capabilities : [];
  const pending = new Set(meta.pending_capabilities || []);
  const visible = caps.filter((c) => CAPABILITY_LABELS[c] !== null);
  if (visible.length === 0) return null;
  return (
    <span
      className="inline-flex items-center gap-1 flex-wrap"
      data-testid={`provider-caps-${meta.name}`}
    >
      <span className="text-slate-300" aria-hidden>·</span>
      {visible.map((c) => {
        const label = CAPABILITY_LABELS[c] || c.replace(/_/g, " ");
        const isPending = pending.has(c);
        return (
          <span
            key={c}
            data-testid={`provider-cap-${meta.name}-${c}`}
            className={`inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium border ${
              isPending
                ? "bg-amber-50 text-amber-700 border-amber-200"
                : "bg-emerald-50 text-emerald-700 border-emerald-200"
            }`}
            title={isPending ? "Pending — carrier API not enabled yet" : `${label} live`}
          >
            {label}{isPending ? " (pending)" : ""}
          </span>
        );
      })}
    </span>
  );
}

function PageHeader() {
  return (
    <div className="mb-6" data-testid="shipping-admin-header">
      <div className="flex items-center gap-3 mb-1">
        <Truck className="w-7 h-7 text-indigo-600" />
        <h1 className="text-3xl font-semibold tracking-tight">Shipping</h1>
        <Badge variant="outline" className="ml-2 text-[10px]">Beta · Mock · USPS · FedEx Sandbox</Badge>
      </div>
      <p className="text-sm text-slate-500">
        Provider-agnostic shipping for your storefronts. Connect carriers, quote rates,
        purchase labels, and track shipments — all from one place.
      </p>
    </div>
  );
}

// =============================================================================
// Provider Credentials Dialog (P1.4b)
// =============================================================================
//
// Tenant enters carrier credentials via PUT /shipping/config/providers/{name}/credentials
// after PUT /shipping/config/providers/{name} sets the desired mode.
//
// Plaintext is held in component state ONLY for the lifetime of one submit;
// on save success the form is cleared so a re-open shows empty inputs.
// Reload shows `credentials_set: true` via the redacted /shipping/config
// response — the UI never echoes plaintext or the *_ref UUIDs.
// =============================================================================

const USPS_CREDENTIAL_FIELDS = [
  { key: "client_id",      label: "Consumer Key (Client ID)",     hint: "From developer.usps.com → your app",       type: "text",     payloadField: "client_id" },
  { key: "client_secret",  label: "Consumer Secret (Client Secret)", hint: "Treated as a secret — never displayed back", type: "password", payloadField: "client_secret" },
  { key: "crid",           label: "CRID",            hint: "Customer Registration ID",                  type: "text",     payloadField: "extras.crid_ref" },
  { key: "mid",            label: "Mailer ID (MID)", hint: "Required for label purchase",               type: "text",     payloadField: "extras.mid_ref" },
  { key: "webhook_secret", label: "Webhook Secret",  hint: "HMAC-SHA256 secret for tracking webhooks",  type: "password", payloadField: "extras.webhook_secret_ref" },
];

// Provider Activation Sprint (Feb 2026) — FedEx-specific credential
// fields. The OAuth client only needs client_id + client_secret; the
// FedEx Account Number is a carrier identifier required by Rates,
// Ship, and Address Validation APIs. Meter Number is optional.
const FEDEX_CREDENTIAL_FIELDS = [
  { key: "client_id",     label: "Client ID (API Key)",       hint: "FedEx Developer → your project's Production / Test key",   type: "text",     payloadField: "client_id" },
  { key: "client_secret", label: "Client Secret",             hint: "Treated as a secret — never displayed back",                type: "password", payloadField: "client_secret" },
  { key: "account",       label: "FedEx Account Number",      hint: "The shipping account associated with your project",        type: "text",     payloadField: "account" },
  { key: "meter_number",  label: "Meter Number (optional)",   hint: "Some legacy projects still need it; usually leave blank",  type: "text",     payloadField: "extras.meter_number_ref" },
];

// Routes credential dialog → field set + payload mapping. New carriers
// register a single entry here; no further branching needed in the
// dialog code below.
const PROVIDER_CREDENTIAL_CONFIG = {
  usps:  { fields: USPS_CREDENTIAL_FIELDS,  defaultMode: "sandbox", docsHint: "developer.usps.com → your app" },
  fedex: { fields: FEDEX_CREDENTIAL_FIELDS, defaultMode: "sandbox", docsHint: "developer.fedex.com → your project" },
};

function CredentialsDialog({ open, onOpenChange, providerName, currentConfig, onSaved }) {
  // Form state — plaintext lives here only while the dialog is open.
  const [mode, setMode] = useState(currentConfig?.mode || "sandbox");
  const [fields, setFields] = useState({});
  const [reveal, setReveal] = useState({});             // per-field show/hide toggle
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);   // { ok, detail, missing[] }

  // Provider Activation Sprint (Feb 2026) — pick the right field set
  // for this carrier; default to USPS for backwards compatibility.
  const providerCfg = PROVIDER_CREDENTIAL_CONFIG[providerName]
    || PROVIDER_CREDENTIAL_CONFIG.usps;
  const activeFields = providerCfg.fields;

  // Reset on open so a re-open never leaks the previous session's plaintext.
  useEffect(() => {
    if (open) {
      setMode(currentConfig?.mode || "sandbox");
      setFields({});
      setReveal({});
      setValidation(null);
    }
  }, [open, currentConfig]);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const toggleReveal = (k) => setReveal((r) => ({ ...r, [k]: !r[k] }));

  const submit = async () => {
    setSaving(true);
    try {
      // Step 1 — persist the mode (sandbox / live) so subsequent
      // credential validation hits the right base URL.
      if (mode !== currentConfig?.mode) {
        await api.put(`/shipping/config/providers/${providerName}`, { mode });
      }
      // Step 2 — persist whichever credential fields the operator filled in.
      // Empty strings are explicitly NOT sent so they don't clear existing
      // refs unintentionally (the backend treats empty as a clear).
      //
      // Provider Activation Sprint (Feb 2026): mapping is now driven by
      // `PROVIDER_CREDENTIAL_CONFIG` so adding a new carrier is purely
      // additive — no per-provider branching in this submit handler.
      const body = { extras: {} };
      const has = (k) => fields[k] !== undefined && fields[k] !== null && fields[k] !== "";
      for (const f of activeFields) {
        if (!has(f.key)) continue;
        const target = f.payloadField || f.key;
        if (target.startsWith("extras.")) {
          body.extras[target.slice("extras.".length)] = fields[f.key];
        } else {
          body[target] = fields[f.key];
        }
      }
      if (!Object.keys(body.extras).length) delete body.extras;

      const haveAnyCred = Object.keys(body).some((k) => k !== "extras")
        || (body.extras && Object.keys(body.extras).length);
      if (haveAnyCred) {
        await api.put(`/shipping/config/providers/${providerName}/credentials`, body);
      }
      toast.success("Credentials saved (encrypted at rest)");
      // Wipe local plaintext immediately on success.
      setFields({});
      setReveal({});
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e); }
    finally { setSaving(false); }
  };

  const validate = async () => {
    setValidating(true);
    setValidation(null);
    try {
      const { data } = await api.post(`/shipping/providers/${providerName}/validate`);
      setValidation(data);
    } catch (e) { toast.error(e); }
    finally { setValidating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`credentials-dialog-${providerName}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-indigo-600" />
            Configure {providerName.toUpperCase()} Credentials
          </DialogTitle>
          <DialogDescription>
            Values are AES-encrypted at rest and never echoed back. Leave a field
            blank to keep the current saved value.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode selector */}
          <div>
            <label className="text-xs font-medium text-slate-600">Mode</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid={`creds-mode-${providerName}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mock">Mock (no live calls)</SelectItem>
                <SelectItem value="sandbox">Sandbox</SelectItem>
                <SelectItem value="live">Live (production)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Credential fields */}
          {activeFields.map((f) => {
            const isSecret = f.type === "password";
            const shown = reveal[f.key];
            return (
              <div key={f.key}>
                <label className="text-xs font-medium text-slate-600">{f.label}</label>
                <div className="relative">
                  <Input
                    type={isSecret && !shown ? "password" : "text"}
                    autoComplete="off"
                    spellCheck={false}
                    value={fields[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder={isSecret ? "•••••••• (leave blank to keep current)" : "Leave blank to keep current"}
                    data-testid={`creds-input-${providerName}-${f.key}`}
                    className={isSecret ? "pr-10 font-mono text-xs" : ""}
                  />
                  {isSecret && (
                    <button
                      type="button"
                      onClick={() => toggleReveal(f.key)}
                      className="absolute inset-y-0 right-0 px-3 text-slate-400 hover:text-slate-700"
                      data-testid={`creds-reveal-${providerName}-${f.key}`}
                      title={shown ? "Hide" : "Show"}
                    >
                      {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">{f.hint}</p>
              </div>
            );
          })}

          {/* Validation result */}
          {validation && (
            <div
              data-testid={`creds-validation-${providerName}`}
              className={`p-3 rounded-lg border text-xs flex items-start gap-2 ${
                validation.ok
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-rose-50 border-rose-200 text-rose-800"
              }`}
            >
              {validation.ok ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <div className="font-medium">
                  {validation.ok ? "Credentials validated" : "Validation failed"}
                </div>
                <div className="mt-0.5">
                  {validation.detail === "oauth_ok_partial_capabilities"
                    ? "OAuth valid — Rates / Labels not fully configured."
                    : validation.detail || (validation.ok ? "Carrier accepted the credentials." : "Unable to validate.")}
                </div>
                {validation.capabilities && (
                  <ul
                    className="mt-1 space-y-0.5"
                    data-testid={`creds-validation-capabilities-${providerName}`}
                  >
                    {[
                      ["oauth", "OAuth token"],
                      ["rate",  "Rate quotes"],
                      ["label", "Label purchase"],
                      ["address_validation", "Address validation"],
                      ["track", "Tracking"],
                    ].map(([k, lbl]) => {
                      const cap = validation.capabilities[k];
                      if (!cap) return null;
                      // FedEx surfaces tracking as deferred — render
                      // that as a distinct line so operators don't
                      // misread it as a credential issue.
                      if (cap.deferred) {
                        return (
                          <li key={k} className="flex items-center gap-1.5">
                            <span aria-hidden="true">•</span>
                            <span className="font-medium">{lbl}:</span>
                            <span>pending (carrier API not enabled)</span>
                          </li>
                        );
                      }
                      return (
                        <li key={k} className="flex items-center gap-1.5">
                          <span aria-hidden="true">{cap.ok ? "✓" : "•"}</span>
                          <span className="font-medium">{lbl}:</span>
                          <span>
                            {cap.ok
                              ? "ready"
                              : `missing ${(cap.missing || []).join(", ") || "credentials"}`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {validation.missing?.length > 0 && (
                  <div className="mt-1">Missing: {validation.missing.join(", ")}</div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={validate}
            disabled={validating || !currentConfig?.credentials_set}
            data-testid={`creds-validate-btn-${providerName}`}
            title={!currentConfig?.credentials_set ? "Save credentials first, then validate" : ""}
          >
            {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
            Validate Credentials
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving}
              data-testid={`creds-save-btn-${providerName}`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================================
// Warehouse / Shipping Profile Dialog (P1.4c)
// =============================================================================
//
// Tenant admins create or edit a `ShippingProfile` (warehouse). All
// required address fields validated client-side before submission.
// Backend's `POST /shipping/config/profiles` + `PUT /shipping/config/profiles/{id}`
// accept the same payload shape.
// =============================================================================

const WAREHOUSE_REQUIRED_FIELDS = [
  "name", "contact_name", "company_name", "street1",
  "city", "state", "postal_code", "country", "phone", "email",
];

function _initialWarehouseDraft(profile) {
  const a = profile?.origin_address || {};
  return {
    name:         profile?.name || "",
    is_default:   !!profile?.is_default,
    contact_name: a.name || "",
    company_name: a.company || "",
    street1:      a.line1 || "",
    street2:      a.line2 || "",
    city:         a.city || "",
    state:        a.state || "",
    postal_code:  a.postal_code || "",
    country:      a.country || "US",
    phone:        a.phone || "",
    email:        a.email || "",
  };
}

function WarehouseDialog({ open, onOpenChange, profile, totalProfiles, onSaved }) {
  const isEdit = !!profile?.id;
  const [draft, setDraft] = useState(() => _initialWarehouseDraft(profile));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setDraft(_initialWarehouseDraft(profile));
      setErrors({});
    }
  }, [open, profile]);

  const setField = (k, v) => {
    setDraft((d) => ({ ...d, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    for (const f of WAREHOUSE_REQUIRED_FIELDS) {
      if (!String(draft[f] ?? "").trim()) e[f] = "Required";
    }
    if (draft.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email)) {
      e.email = "Invalid email";
    }
    if (draft.country && draft.country.length !== 2) {
      e.country = "2-letter ISO code";
    }
    if (draft.postal_code && draft.postal_code.trim().length < 3) {
      e.postal_code = "Too short";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      toast.error("Fix the highlighted fields");
      return;
    }
    setSaving(true);
    try {
      const origin = {
        name:        draft.contact_name.trim(),
        company:     draft.company_name.trim(),
        line1:       draft.street1.trim(),
        line2:       draft.street2.trim() || null,
        city:        draft.city.trim(),
        state:       draft.state.trim().toUpperCase(),
        postal_code: draft.postal_code.trim(),
        country:     draft.country.trim().toUpperCase(),
        phone:       draft.phone.trim(),
        email:       draft.email.trim(),
      };
      const body = {
        name: draft.name.trim(),
        is_default: draft.is_default,
        origin_address: origin,
      };
      if (isEdit) {
        await api.put(`/shipping/config/profiles/${profile.id}`, body);
        toast.success(`Warehouse "${body.name}" updated`);
      } else {
        await api.post("/shipping/config/profiles", body);
        toast.success(`Warehouse "${body.name}" added`);
      }
      onSaved?.();
    } catch (e) { toast.error(e); }
    finally { setSaving(false); }
  };

  const fieldClass = (k) =>
    `text-xs font-medium ${errors[k] ? "text-rose-600" : "text-slate-600"}`;
  const inputClass = (k) => errors[k] ? "border-rose-400 focus-visible:ring-rose-400" : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="warehouse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-indigo-600" />
            {isEdit ? "Edit Warehouse" : "Add Warehouse"}
          </DialogTitle>
          <DialogDescription>
            Origin address used for rate quoting and label purchase. All
            fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={fieldClass("name")}>Warehouse name *</label>
            <Input
              data-testid="warehouse-name-input"
              value={draft.name}
              placeholder="Main Warehouse"
              className={inputClass("name")}
              onChange={(e) => setField("name", e.target.value)}
            />
            {errors.name && <div className="text-[11px] text-rose-600 mt-0.5">{errors.name}</div>}
          </div>

          <div>
            <label className={fieldClass("contact_name")}>Contact name *</label>
            <Input
              data-testid="warehouse-contact-name-input"
              value={draft.contact_name}
              placeholder="Jane Doe"
              className={inputClass("contact_name")}
              onChange={(e) => setField("contact_name", e.target.value)}
            />
            {errors.contact_name && <div className="text-[11px] text-rose-600 mt-0.5">{errors.contact_name}</div>}
          </div>
          <div>
            <label className={fieldClass("company_name")}>Company name *</label>
            <Input
              data-testid="warehouse-company-name-input"
              value={draft.company_name}
              placeholder="Acme Inc."
              className={inputClass("company_name")}
              onChange={(e) => setField("company_name", e.target.value)}
            />
            {errors.company_name && <div className="text-[11px] text-rose-600 mt-0.5">{errors.company_name}</div>}
          </div>

          <div className="col-span-2">
            <label className={fieldClass("street1")}>Street address line 1 *</label>
            <Input
              data-testid="warehouse-street1-input"
              value={draft.street1}
              placeholder="100 Main St"
              className={inputClass("street1")}
              onChange={(e) => setField("street1", e.target.value)}
            />
            {errors.street1 && <div className="text-[11px] text-rose-600 mt-0.5">{errors.street1}</div>}
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-slate-600">Street address line 2</label>
            <Input
              data-testid="warehouse-street2-input"
              value={draft.street2}
              placeholder="Suite 200 (optional)"
              onChange={(e) => setField("street2", e.target.value)}
            />
          </div>

          <div>
            <label className={fieldClass("city")}>City *</label>
            <Input
              data-testid="warehouse-city-input"
              value={draft.city} placeholder="Austin"
              className={inputClass("city")}
              onChange={(e) => setField("city", e.target.value)}
            />
            {errors.city && <div className="text-[11px] text-rose-600 mt-0.5">{errors.city}</div>}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className={fieldClass("state")}>State *</label>
              <Input
                data-testid="warehouse-state-input"
                value={draft.state} placeholder="TX" maxLength={3}
                className={inputClass("state")}
                onChange={(e) => setField("state", e.target.value.toUpperCase())}
              />
              {errors.state && <div className="text-[11px] text-rose-600 mt-0.5">{errors.state}</div>}
            </div>
            <div>
              <label className={fieldClass("postal_code")}>ZIP *</label>
              <Input
                data-testid="warehouse-postal-code-input"
                value={draft.postal_code} placeholder="73301"
                className={inputClass("postal_code")}
                onChange={(e) => setField("postal_code", e.target.value)}
              />
              {errors.postal_code && <div className="text-[11px] text-rose-600 mt-0.5">{errors.postal_code}</div>}
            </div>
            <div>
              <label className={fieldClass("country")}>Country *</label>
              <Input
                data-testid="warehouse-country-input"
                value={draft.country} placeholder="US" maxLength={2}
                className={inputClass("country")}
                onChange={(e) => setField("country", e.target.value.toUpperCase())}
              />
              {errors.country && <div className="text-[11px] text-rose-600 mt-0.5">{errors.country}</div>}
            </div>
          </div>

          <div>
            <label className={fieldClass("phone")}>Phone *</label>
            <Input
              data-testid="warehouse-phone-input"
              value={draft.phone} placeholder="+1 555 123 4567"
              className={inputClass("phone")}
              onChange={(e) => setField("phone", e.target.value)}
            />
            {errors.phone && <div className="text-[11px] text-rose-600 mt-0.5">{errors.phone}</div>}
          </div>
          <div>
            <label className={fieldClass("email")}>Email *</label>
            <Input
              data-testid="warehouse-email-input"
              type="email" value={draft.email} placeholder="ops@acme.com"
              className={inputClass("email")}
              onChange={(e) => setField("email", e.target.value)}
            />
            {errors.email && <div className="text-[11px] text-rose-600 mt-0.5">{errors.email}</div>}
          </div>

          <div className="col-span-2 flex items-center gap-3 pt-2 border-t border-slate-100">
            <Switch
              checked={draft.is_default}
              onCheckedChange={(v) => setField("is_default", v)}
              data-testid="warehouse-default-toggle"
              disabled={isEdit && profile.is_default && totalProfiles <= 1}
              title={
                isEdit && profile.is_default && totalProfiles <= 1
                  ? "Single warehouse is always default"
                  : ""
              }
            />
            <div className="text-xs">
              <div className="font-medium text-slate-700">Default origin</div>
              <div className="text-[11px] text-slate-500">
                Used for rate quoting and label purchase when no profile is pinned.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="warehouse-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
            {isEdit ? "Save Changes" : "Add Warehouse"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================================
// Settings tab
// =============================================================================

function SettingsPanel({ config, providers, onChanged }) {
  const [savingTop, setSavingTop] = useState(false);
  const [credsDialogProvider, setCredsDialogProvider] = useState(null);
  // P1.4c — warehouse dialog. `null` = closed, `{}` = creating new,
  // `{...existing profile}` = editing.
  const [editingProfile, setEditingProfile] = useState(null);

  const updateTopLevel = async (patch) => {
    setSavingTop(true);
    try {
      await api.put("/shipping/config", patch);
      toast.success("Settings updated");
      onChanged?.();
    } catch (e) {
      toast.error(e);
    } finally {
      setSavingTop(false);
    }
  };

  const toggleProvider = async (name, enabled) => {
    try {
      await api.put(`/shipping/config/providers/${name}`, { enabled });
      toast.success(`${name.toUpperCase()} ${enabled ? "enabled" : "disabled"}`);
      onChanged?.();
    } catch (e) {
      toast.error(e);
    }
  };

  const addProfile = () => setEditingProfile({});         // open dialog in create mode
  const editProfile = (p) => setEditingProfile(p);          // open dialog in edit mode

  const removeProfile = async (id) => {
    try {
      await api.delete(`/shipping/config/profiles/${id}`);
      toast.success("Warehouse removed");
      onChanged?.();
    } catch (e) { toast.error(e); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="shipping-settings-panel">
      {/* Default provider + strategy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="w-4 h-4 text-indigo-600" /> Routing Strategy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600">Default provider</label>
            <Select
              value={config.default_provider}
              onValueChange={(v) => updateTopLevel({ default_provider: v })}
              disabled={savingTop}
            >
              <SelectTrigger data-testid="default-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.keys(config.providers || {}).map((n) => (
                  <SelectItem key={n} value={n}>{n.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Carrier selection strategy</label>
            <Select
              value={config.carrier_selection_strategy}
              onValueChange={(v) => updateTopLevel({ carrier_selection_strategy: v })}
              disabled={savingTop}
            >
              <SelectTrigger data-testid="strategy-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STRATEGY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1.5">
              Controls how rate quoting picks between enabled providers when the caller
              doesn&apos;t pin a specific carrier.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Providers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Truck className="w-4 h-4 text-indigo-600" /> Carriers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(config.providers || {}).map(([name, p]) => {
            const meta = providers.find((x) => x.name === name) || {};
            const isMock = name === "mock";
            return (
              <div
                key={name}
                data-testid={`provider-row-${name}`}
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-slate-100 grid place-items-center">
                    <Truck className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{p.label || name.toUpperCase()}</span>
                      {!meta.implemented && (
                        <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                          stub
                        </Badge>
                      )}
                      {p.credentials_set && (
                        <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300 bg-emerald-50 inline-flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" /> credentials set
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                      <span>mode · {p.mode}</span>
                      {p.mode === "live" && (
                        <Badge
                          variant="outline"
                          className="text-[10px] font-semibold text-red-700 border-red-400 bg-red-50 uppercase tracking-wide"
                          data-testid={`provider-live-badge-${p.name}`}
                          title="This provider will create REAL carrier charges. Verify SHIPPING_ALLOW_LIVE_MODE=true in the environment."
                        >
                          LIVE MODE
                        </Badge>
                      )}
                      <CarrierCapabilityBadges meta={meta} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* P1.4b — Configure button. Provider Activation
                      Sprint (Feb 2026): USPS + FedEx are wired live;
                      UPS remains a stub so its button stays disabled
                      with a clear tooltip. */}
                  {!isMock && (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`provider-configure-${name}`}
                      onClick={() => setCredsDialogProvider(name)}
                      disabled={!PROVIDER_CREDENTIAL_CONFIG[name]}
                      title={PROVIDER_CREDENTIAL_CONFIG[name]
                        ? "Configure credentials"
                        : "Provider stub — implementation pending"}
                    >
                      <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                      {p.credentials_set ? "Update" : "Configure"}
                    </Button>
                  )}
                  <Switch
                    data-testid={`provider-toggle-${name}`}
                    checked={p.enabled}
                    onCheckedChange={(v) => toggleProvider(name, v)}
                    disabled={!isMock && !meta.implemented && !p.credentials_set}
                    title={!isMock && !meta.implemented ? "Provider stub — implementation pending" : ""}
                  />
                </div>
              </div>
            );
          })}
          <p className="text-[11px] text-slate-500">
            USPS and FedEx are wired live (sandbox + production); UPS remains
            a provider stub. All carriers require tenant-owned credentials,
            encrypted at rest — the UI never displays plaintext or secret refs.
          </p>
        </CardContent>
      </Card>

      {/* Shipping profiles (warehouses) */}
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-4 h-4 text-indigo-600" /> Warehouses / Origin Addresses
          </CardTitle>
          <Button
            size="sm"
            data-testid="profile-add-btn"
            onClick={addProfile}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            + Add Warehouse
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2" data-testid="profiles-list">
            {(config.shipping_profiles || []).map((p) => {
              const a = p.origin_address || {};
              const hasFullAddress = a.line1 && a.city && a.state && a.postal_code;
              const onlyOrDefault = p.is_default || (config.shipping_profiles.length <= 1);
              return (
                <div
                  key={p.id}
                  className="p-3 rounded-lg border border-slate-200 bg-slate-50"
                  data-testid={`profile-row-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                        {p.name}
                        {p.is_default && (
                          <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-300 bg-indigo-50">
                            default
                          </Badge>
                        )}
                        {!p.enabled && (
                          <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-300">disabled</Badge>
                        )}
                        {!hasFullAddress && (
                          <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">
                            incomplete address
                          </Badge>
                        )}
                      </div>
                      {hasFullAddress ? (
                        <div className="text-[12px] text-slate-600 mt-1 leading-tight">
                          {a.name && <div className="text-[11px] text-slate-500">{a.name}{a.company ? ` · ${a.company}` : ""}</div>}
                          <div>{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
                          <div>{a.city}, {a.state} {a.postal_code} {a.country}</div>
                          {(a.phone || a.email) && (
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {a.phone}{a.phone && a.email ? " · " : ""}{a.email}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-[11px] text-amber-700 mt-1">
                          No origin address — labels and rates can&apos;t use this warehouse until configured.
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`profile-edit-${p.id}`}
                        onClick={() => editProfile(p)}
                      >
                        Edit
                      </Button>
                      {!p.is_default && (
                        <Button
                          variant="ghost" size="sm"
                          data-testid={`profile-make-default-${p.id}`}
                          onClick={async () => {
                            try {
                              await api.put(`/shipping/config/profiles/${p.id}`, { is_default: true });
                              toast.success(`${p.name} is now the default warehouse`);
                              onChanged?.();
                            } catch (e) { toast.error(e); }
                          }}
                        >
                          Make default
                        </Button>
                      )}
                      {!onlyOrDefault && (
                        <Button
                          data-testid={`profile-remove-${p.id}`}
                          variant="ghost" size="sm"
                          onClick={() => removeProfile(p.id)}
                          className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {(config.shipping_profiles?.length || 0) <= 1 && (
            <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-md p-2.5 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                The default warehouse cannot be deleted while it&apos;s the only one.
                Add a second warehouse first, then mark it as default to remove the original.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* P1.4b — credentials dialog. Mounted at the SettingsPanel root so
          opening from any provider row works without an additional portal. */}
      {credsDialogProvider && (
        <CredentialsDialog
          open={!!credsDialogProvider}
          onOpenChange={(v) => { if (!v) setCredsDialogProvider(null); }}
          providerName={credsDialogProvider}
          currentConfig={(config.providers || {})[credsDialogProvider] || {}}
          onSaved={onChanged}
        />
      )}

      {/* P1.4c — warehouse edit/create dialog. */}
      {editingProfile !== null && (
        <WarehouseDialog
          open={editingProfile !== null}
          onOpenChange={(v) => { if (!v) setEditingProfile(null); }}
          profile={editingProfile}
          totalProfiles={(config.shipping_profiles || []).length}
          onSaved={() => { setEditingProfile(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Test Tools tab — rate quote + label purchase
// =============================================================================

const SAMPLE_PAYLOAD = {
  to: { line1: "1 Public Sq", city: "New York", state: "NY", postal_code: "10001", country: "US" },
  from: { line1: "100 Main St", city: "Austin", state: "TX", postal_code: "73301", country: "US" },
};

function TestToolsPanel({ config, onShipmentCreated }) {
  const [provider, setProvider] = useState("mock");
  const [quoting, setQuoting] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [quotes, setQuotes] = useState([]);
  // P1.4d — when USPS replies 2xx with zero usable rates the backend
  // attaches a redacted diagnostics envelope under `meta.diagnostics`.
  // We surface it inline so operators stop guessing.
  const [lastQuoteEnvelope, setLastQuoteEnvelope] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [pickedService, setPickedService] = useState("");
  const [lastLabel, setLastLabel] = useState(null);
  // FedEx Live Cutover follow-up (Feb 2026) — allow the operator to
  // pipe an address from the Address Validation panel below into the
  // Rate Quote Tester + Purchase Label test payload, rather than using
  // the hardcoded demo NYC destination. `pipedDestination` is set by
  // the Address Validation panel; `useProfileFromValidation` toggles
  // whether that address overrides SAMPLE_PAYLOAD.to at request time.
  const [pipedDestination, setPipedDestination] = useState(null);
  const [useProfileFromValidation, setUseProfileFromValidation] = useState(false);
  // P1.4c — operator can pin the warehouse used for test quotes / labels.
  // Defaults to the tenant's default profile when present.
  const profiles = config.shipping_profiles || [];
  const defaultProfile = profiles.find((p) => p.is_default) || profiles[0];
  const [pickedProfileId, setPickedProfileId] = useState(defaultProfile?.id || "");

  const enabledProviderNames = useMemo(
    () => Object.entries(config.providers || {}).filter(([, p]) => p.enabled).map(([n]) => n),
    [config],
  );

  // Resolve the actual from_address to send. When the picked profile has a
  // complete origin_address, use it; otherwise fall back to the demo route
  // so tests still work on a freshly created tenant.
  const pickedProfile = profiles.find((p) => p.id === pickedProfileId) || defaultProfile;
  const profileOrigin = pickedProfile?.origin_address || null;
  const hasProfileOrigin = !!(profileOrigin && profileOrigin.line1 && profileOrigin.city && profileOrigin.state && profileOrigin.postal_code);
  const fromAddress = hasProfileOrigin ? {
    name:        profileOrigin.name,
    company:     profileOrigin.company,
    line1:       profileOrigin.line1,
    line2:       profileOrigin.line2,
    city:        profileOrigin.city,
    state:       profileOrigin.state,
    postal_code: profileOrigin.postal_code,
    country:     profileOrigin.country || "US",
    phone:       profileOrigin.phone,
    email:       profileOrigin.email,
  } : SAMPLE_PAYLOAD.from;

  // Provider Activation Sprint (Feb 2026) — gate FedEx rates/labels
  // behind a complete warehouse origin (Sprint Requirement #8). The
  // backend already returns `no_origin_address_resolved` when no
  // profile is configured, but FedEx specifically needs a complete
  // contact + address (phone, name, etc.) to mint a label, so we
  // surface the prompt up front rather than waiting for a 4xx.
  const NEEDS_FULL_ORIGIN = new Set(["fedex"]);
  const isFullOrigin = !!(
    hasProfileOrigin
    && profileOrigin?.name
    && profileOrigin?.phone
  );
  const warehouseBlocksFedex = NEEDS_FULL_ORIGIN.has(provider) && !isFullOrigin;
  const warehouseBlockMessage = "Complete your default warehouse/origin address before requesting FedEx rates or labels.";

  // Resolve the destination the way the request will actually be sent:
  // when the operator has opted in AND has piped an address from the
  // Address Validation panel below, use that; otherwise fall back to
  // the demo NYC destination. We keep a boolean around so the UI can
  // clearly surface which one is active.
  const pipedDestinationComplete = !!(
    pipedDestination
    && pipedDestination.line1
    && pipedDestination.city
    && pipedDestination.state
    && pipedDestination.postal_code
  );
  const destinationActive = useProfileFromValidation && pipedDestinationComplete
    ? pipedDestination
    : SAMPLE_PAYLOAD.to;
  const destinationIsPiped = destinationActive !== SAMPLE_PAYLOAD.to;

  const runQuote = async () => {
    if (warehouseBlocksFedex) {
      toast.error(warehouseBlockMessage);
      return;
    }
    setQuoting(true);
    setQuotes([]);
    setLastQuoteEnvelope(null);
    setShowDiagnostics(false);
    try {
      const { data } = await api.post("/shipping/rates", {
        provider,
        to_address: destinationActive,
        from_address: fromAddress,
        shipping_profile_id: hasProfileOrigin ? pickedProfileId : null,
        packages: [{ weight: 12, weight_unit: "oz", length: 10, width: 6, height: 2 }],
      });
      setLastQuoteEnvelope(data);
      if (data.ok) {
        setQuotes(data.quotes || []);
        if (!data.quotes?.length) {
          toast.message("No quotes returned");
          // Auto-expand the diagnostic panel when carrier diagnostics
          // are present — they're the actionable signal.
          if ((data?.meta?.diagnostics || []).length > 0) {
            setShowDiagnostics(true);
          }
        }
      } else {
        toast.error(data.detail || data.error || "Rate request failed");
      }
    } catch (e) { toast.error(e); }
    finally { setQuoting(false); }
  };

  // Provider Activation follow-up (Feb 2026) — when the server-side
  // FedEx address gate blocks the label, the response carries
  // `error: "address_validation_blocked"` + `validation: {...}` (the
  // full Address Validation envelope). We pop a modal that lets the
  // operator either fix the address or, when the block is
  // `suggestion_pending_review`, explicitly acknowledge the
  // suggestion and re-submit.
  const [gateBlock, setGateBlock] = useState(null);    // { code, which, message, validation }

  const purchaseLabel = async ({ withAck = false } = {}) => {
    if (!pickedService) { toast.error("Pick a service first"); return; }
    if (warehouseBlocksFedex) {
      toast.error(warehouseBlockMessage);
      return;
    }
    setPurchasing(true);
    try {
      const { data } = await api.post("/shipping/labels", {
        provider,
        service_code: pickedService,
        to_address: destinationActive,
        from_address: fromAddress,
        shipping_profile_id: hasProfileOrigin ? pickedProfileId : null,
        packages: [{ weight: 12, weight_unit: "oz", length: 10, width: 6, height: 2 }],
        order_id: `test-${Date.now()}`,
        idempotency_key: `test-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        address_validation_ack: !!withAck,
      });
      if (!data.ok) {
        if (data.error === "address_validation_blocked") {
          setGateBlock({
            code: data.code,
            which: data.which,
            message: data.detail || "FedEx could not verify this address.",
            validation: data.validation || {},
          });
          // Don't toast for blocked gate — the modal speaks for itself.
        } else {
          toast.error(data.detail || data.error || "Label purchase failed");
        }
      } else {
        toast.success(`Label ${data.label?.tracking_number}`);
        setLastLabel(data);
        setGateBlock(null);
        onShipmentCreated?.(data.shipment?.id);
      }
    } catch (e) { toast.error(e); }
    finally { setPurchasing(false); }
  };

  const downloadLabel = (labelId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/shipping/labels/${labelId}/download`;
    const token = localStorage.getItem("ren_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${labelId}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Label download failed"));
  };

  return (
    <div className="space-y-6" data-testid="shipping-test-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FlaskConical className="w-4 h-4 text-indigo-600" /> Rate Quote Tester
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs font-medium text-slate-600">Provider</label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger data-testid="test-provider-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {enabledProviderNames.map((n) => (
                    <SelectItem key={n} value={n}>{n.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {profiles.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs font-medium text-slate-600">Warehouse</label>
                <Select value={pickedProfileId} onValueChange={setPickedProfileId}>
                  <SelectTrigger data-testid="test-profile-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{p.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="get-rates-btn" onClick={runQuote} disabled={quoting}>
              {quoting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Get Rates
            </Button>
            {provider === "fedex" && (
              <LastFedexResponseButton />
            )}
          </div>
          <div
            className="text-[11px] text-slate-500"
            data-testid="test-tools-origin-banner"
          >
            Origin · {hasProfileOrigin ? (
              <>
                <span className="font-medium text-slate-700">{pickedProfile?.name}</span>
                {" "}— {fromAddress.city}, {fromAddress.state} {fromAddress.postal_code}
              </>
            ) : (
              <>
                <span className="text-amber-700">demo origin</span> (configure a warehouse to use your own
                address — Settings → Warehouses)
              </>
            )}.
            {" "}Destination ·{" "}
            {destinationIsPiped ? (
              <span
                className="font-medium text-emerald-700"
                data-testid="test-tools-destination-piped"
              >
                {destinationActive.line1}, {destinationActive.city} {destinationActive.state}{" "}
                {destinationActive.postal_code} · from Address Validation
              </span>
            ) : (
              <span data-testid="test-tools-destination-demo">
                New York NY 10001 · demo destination
              </span>
            )}
            , 12 oz package.
          </div>
          {pipedDestinationComplete && (
            <label
              className="flex items-start gap-2 text-[11px] text-slate-600 bg-indigo-50/60 border border-indigo-100 rounded-md px-3 py-2"
              data-testid="test-tools-use-validation-toggle-wrap"
            >
              <input
                type="checkbox"
                checked={useProfileFromValidation}
                onChange={(e) => setUseProfileFromValidation(e.target.checked)}
                data-testid="test-tools-use-validation-toggle"
                className="mt-0.5"
              />
              <span>
                Use the address I entered in <b>Address Validation</b> below as the
                destination for Rate Quote &amp; Purchase Label tests.
                {" "}
                <button
                  type="button"
                  onClick={() => { setPipedDestination(null); setUseProfileFromValidation(false); }}
                  data-testid="test-tools-clear-piped-destination"
                  className="ml-1 underline hover:no-underline text-slate-500"
                >
                  Clear
                </button>
              </span>
            </label>
          )}
          {warehouseBlocksFedex && (
            <div
              data-testid="fedex-warehouse-block"
              className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs p-3"
            >
              <strong>{warehouseBlockMessage}</strong>{" "}
              FedEx needs a complete origin (contact name + phone) before quoting
              rates or printing labels. Open <em>Settings → Warehouses</em> and
              fill in the default profile.
            </div>
          )}

          {quotes.length === 0 && lastQuoteEnvelope && (
            <div
              data-testid="rate-diagnostics-panel"
              className="mt-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs"
            >
              <button
                type="button"
                onClick={() => setShowDiagnostics((v) => !v)}
                data-testid="rate-diagnostics-toggle"
                className="w-full flex items-center justify-between px-3 py-2 font-medium hover:bg-amber-100/60"
              >
                <span>
                  Rate diagnostics
                  {(lastQuoteEnvelope?.meta?.diagnostics || []).length > 0
                    ? ` · ${lastQuoteEnvelope.meta.diagnostics.length} provider`
                    : ""}
                </span>
                <span aria-hidden="true">{showDiagnostics ? "▾" : "▸"}</span>
              </button>
              {showDiagnostics && (
                <div className="px-3 pb-3 space-y-3" data-testid="rate-diagnostics-body">
                  <div>
                    <div className="font-semibold text-[11px] uppercase tracking-wide">Provider(s)</div>
                    <div>
                      {(lastQuoteEnvelope?.meta?.providers_queried
                        || (lastQuoteEnvelope?.meta?.pinned ? [lastQuoteEnvelope.meta.pinned] : [])
                        || (lastQuoteEnvelope?.meta?.default_provider ? [lastQuoteEnvelope.meta.default_provider] : [])
                        || []).join(", ") || "—"}
                    </div>
                  </div>
                  {(lastQuoteEnvelope?.errors || []).length > 0 && (
                    <div>
                      <div className="font-semibold text-[11px] uppercase tracking-wide">Provider errors</div>
                      <pre className="whitespace-pre-wrap break-all text-[11px] bg-white/60 rounded p-2 border border-amber-200">
                        {JSON.stringify(lastQuoteEnvelope.errors, null, 2)}
                      </pre>
                    </div>
                  )}
                  {(lastQuoteEnvelope?.meta?.diagnostics || []).map((d, i) => (
                    <div key={i} className="space-y-2" data-testid={`rate-diagnostic-${i}`}>
                      <div className="font-semibold text-[11px] uppercase tracking-wide">
                        Carrier-level diagnostic — {d.provider}
                        {d.mail_class && (
                          <span className="ml-1 font-normal normal-case tracking-normal">
                            · {d.mail_class}
                          </span>
                        )}
                        {Number.isInteger(d.status_code) && (
                          <span className="ml-1 font-normal normal-case tracking-normal">
                            · HTTP {d.status_code}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px]">{d.hint}</div>
                      {d.endpoint_url && (
                        <div>
                          <div className="font-medium">Endpoint URL called:</div>
                          <pre className="whitespace-pre-wrap break-all text-[11px] bg-white/60 rounded p-2 border border-amber-200">
                            {d.endpoint_url}
                          </pre>
                        </div>
                      )}
                      <div>
                        <div className="font-medium">Request sent (redacted):</div>
                        <pre className="whitespace-pre-wrap break-all text-[11px] bg-white/60 rounded p-2 border border-amber-200">
                          {JSON.stringify(d.request_body, null, 2)}
                        </pre>
                      </div>
                      {d.response_headers && (
                        <div>
                          <div className="font-medium">{(d.provider || "Carrier").toUpperCase()} response headers (sanitized):</div>
                          <pre className="whitespace-pre-wrap break-all text-[11px] bg-white/60 rounded p-2 border border-amber-200">
                            {JSON.stringify(d.response_headers, null, 2)}
                          </pre>
                        </div>
                      )}
                      {(d.response_body_json !== undefined && d.response_body_json !== null) && (
                        <div>
                          <div className="font-medium">{(d.provider || "Carrier").toUpperCase()} response body (verbatim, JSON):</div>
                          <pre className="whitespace-pre-wrap break-all text-[11px] bg-white/60 rounded p-2 border border-amber-200">
                            {JSON.stringify(d.response_body_json, null, 2)}
                          </pre>
                        </div>
                      )}
                      {d.response_body_raw && d.response_body_json == null && (
                        <div>
                          <div className="font-medium">{(d.provider || "Carrier").toUpperCase()} response body (verbatim, raw):</div>
                          <pre className="whitespace-pre-wrap break-all text-[11px] bg-white/60 rounded p-2 border border-amber-200">
                            {d.response_body_raw}
                          </pre>
                        </div>
                      )}
                      {d.oauth_token_meta && (
                        <div>
                          <div className="font-medium">OAuth token acquisition:</div>
                          <pre className="whitespace-pre-wrap break-all text-[11px] bg-white/60 rounded p-2 border border-amber-200">
                            {JSON.stringify(d.oauth_token_meta, null, 2)}
                          </pre>
                        </div>
                      )}
                      <div>
                        <div className="font-medium">Carrier response shape (summary):</div>
                        <pre className="whitespace-pre-wrap break-all text-[11px] bg-white/60 rounded p-2 border border-amber-200">
                          {JSON.stringify(d.response_shape, null, 2)}
                        </pre>
                      </div>
                    </div>
                  ))}
                  {(lastQuoteEnvelope?.meta?.diagnostics || []).length === 0
                    && (lastQuoteEnvelope?.errors || []).length === 0 && (
                    <div className="text-[11px] italic">
                      No carrier-level diagnostics were returned for this request.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {quotes.length > 0 && (
            <div className="space-y-2" data-testid="quotes-list">
              {quotes.map((q) => (
                <label
                  key={`${q.provider}-${q.service_code}`}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50"
                  data-testid={`quote-row-${q.service_code}`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="picked-service"
                      checked={pickedService === q.service_code}
                      onChange={() => setPickedService(q.service_code)}
                      data-testid={`quote-pick-${q.service_code}`}
                    />
                    <div>
                      <div className="font-medium text-sm">{q.service_name}</div>
                      <div className="text-[11px] text-slate-500">
                        {q.provider.toUpperCase()} · {q.service_code} · est {q.estimated_days ?? "—"} day(s)
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm">${q.amount.toFixed(2)} {q.currency}</div>
                  </div>
                </label>
              ))}
              <Button
                data-testid="purchase-label-btn"
                onClick={() => purchaseLabel({})}
                disabled={!pickedService || purchasing}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {purchasing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Purchase Label
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {lastLabel?.label && (
        <Card data-testid="last-label-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="w-4 h-4 text-emerald-600" /> Last Label
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{lastLabel.label.tracking_number}</div>
                <div className="text-[11px] text-slate-500">
                  {lastLabel.shipment.provider.toUpperCase()} ·
                  {" "}${lastLabel.label.rate_amount?.toFixed(2)} {lastLabel.label.rate_currency} ·
                  {" "}{lastLabel.shipment.service_code}
                </div>
              </div>
              <Button
                data-testid="download-last-label-btn"
                onClick={() => downloadLabel(lastLabel.label.id)}
                variant="outline"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Download PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Provider Activation Sprint (Feb 2026) — Address Validation
          Sandbox. FedEx is the only carrier that exposes the
          capability today; other providers respond with
          `capability_not_supported` and the panel hides the run button
          accordingly. */}
      <AddressValidationPanel
        provider={provider}
        onPipeAddress={(addr) => {
          setPipedDestination(addr);
          setUseProfileFromValidation(true);
          toast.success("Address piped into Rate Quote Tester");
        }}
      />

      {gateBlock && (
        <FedexAddressGateModal
          block={gateBlock}
          purchasing={purchasing}
          onClose={() => setGateBlock(null)}
          onAcceptSuggestion={() => {
            setGateBlock(null);
            purchaseLabel({ withAck: true });
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Last FedEx Response button (Provider Activation follow-up — Feb 2026)
// -----------------------------------------------------------------------------
// Fetches the most-recent raw FedEx response pinned per tenant by the
// FedEx provider. Lets operators compare responses across attempts
// (rate / label / address validation) without re-running the request.
// =============================================================================
function LastFedexResponseButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(
        "/shipping/providers/fedex/last-diagnostic",
      );
      setData(resp?.diagnostic || {});
    } catch (e) { toast.error(e); }
    finally { setLoading(false); }
  };

  const onClick = async () => {
    if (!open) await load();
    setOpen((v) => !v);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={loading}
      data-testid="last-fedex-response-btn"
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
      {open ? "Hide last FedEx response" : "Show last FedEx response"}
      <LastFedexResponseViewer
        open={open}
        data={data}
        onClose={() => setOpen(false)}
        onRefresh={load}
        refreshing={loading}
      />
    </Button>
  );
}

function LastFedexResponseViewer({ open, data, onClose, onRefresh, refreshing }) {
  if (!open) return null;
  const ops = data && Object.keys(data).length > 0 ? data : null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto"
      data-testid="last-fedex-response-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative z-10 w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-slate-200 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">FedEx Diagnostic</div>
            <h2 className="text-lg font-semibold tracking-tight">Last raw FedEx responses</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Pinned per tenant — only the most recent response for each operation is retained.
              Customers never see this view.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="last-fedex-refresh">
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
              Refresh
            </Button>
            <button
              type="button"
              onClick={onClose}
              data-testid="last-fedex-close"
              className="text-slate-400 hover:text-slate-700 p-1 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {!ops ? (
          <div className="text-sm text-slate-500 py-10 text-center" data-testid="last-fedex-empty">
            No diagnostics captured yet. Run a Rate Quote, Label, or Address Validation against FedEx — the response will be pinned here automatically.
          </div>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {["rate", "label", "validate_address"].map((opKey) => {
              const op = ops[opKey];
              if (!op) return null;
              const ok = !!op.ok;
              return (
                <details
                  key={opKey}
                  className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50/40"
                  data-testid={`last-fedex-op-${opKey}`}
                  open
                >
                  <summary className={`cursor-pointer px-3 py-2 text-sm font-medium flex items-center justify-between ${ok ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`}>
                    <span>
                      {ok ? "✓" : "⚠"} {opKey.replace("_", " ")}
                      {op.status_code ? ` · HTTP ${op.status_code}` : ""}
                    </span>
                    <span className="text-[10px] font-mono text-slate-500">
                      {op.captured_at ? new Date(op.captured_at).toLocaleString() : ""}
                    </span>
                  </summary>
                  <div className="p-3 space-y-2 text-[11px]">
                    {op.endpoint && (
                      <div>
                        <div className="font-medium text-slate-600">Endpoint</div>
                        <div className="font-mono break-all">{op.endpoint}</div>
                      </div>
                    )}
                    {op.request && (
                      <div>
                        <div className="font-medium text-slate-600">Request (redacted)</div>
                        <pre className="whitespace-pre-wrap break-all bg-white rounded p-2 border border-slate-200 max-h-60 overflow-auto">
                          {JSON.stringify(op.request, null, 2)}
                        </pre>
                      </div>
                    )}
                    {op.response && (
                      <div>
                        <div className="font-medium text-slate-600">Response (verbatim)</div>
                        <pre className="whitespace-pre-wrap break-all bg-white rounded p-2 border border-slate-200 max-h-72 overflow-auto">
                          {JSON.stringify(op.response, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// FedEx Address Gate modal (Provider Activation follow-up — Feb 2026)
// -----------------------------------------------------------------------------
// Surfaces the structured envelope returned by the server-side gate
// when it refuses to mint a FedEx label. Two paths:
//   * code = "country_drift"           → hard block, no Accept button.
//   * code = "suggestion_pending_review" → operator can review the
//     submitted-vs-suggested comparison and click "Accept FedEx
//     suggestion and continue" which re-submits with
//     `address_validation_ack: true`.
// Raw FedEx response is shown via a collapsible diagnostic panel —
// admins only; never reaches the customer-facing flow.
// =============================================================================
function FedexAddressGateModal({ block, purchasing, onClose, onAcceptSuggestion }) {
  const [showRaw, setShowRaw] = useState(false);
  const v = block.validation || {};
  const submitted = v.submitted || {};
  const suggestion = v.suggestion || null;
  const matches = v.suggestion_matches || {};
  const hardBlock = block.code === "country_drift";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center px-4 py-8 overflow-y-auto"
      data-testid="fedex-gate-modal"
    >
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className={`text-xs uppercase tracking-wider ${hardBlock ? "text-rose-700" : "text-amber-700"}`}>
              {hardBlock ? "Label blocked — address could not be verified" : "Label paused — FedEx suggested changes"}
            </div>
            <h2 className="text-lg font-semibold tracking-tight">
              {block.which === "origin" ? "Origin address review" : "Destination address review"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="fedex-gate-close"
            className="text-slate-400 hover:text-slate-700 p-1 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          data-testid="fedex-gate-message"
          className={`text-sm p-3 rounded-md border ${hardBlock ? "bg-rose-50 border-rose-200 text-rose-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}
        >
          {block.message}
        </div>

        {suggestion && (
          <div className="mt-4 bg-white rounded-md border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-[100px_1fr_1fr] text-[11px]">
              <div className="px-2 py-1.5 font-semibold bg-slate-50 border-b border-slate-200">Field</div>
              <div className="px-2 py-1.5 font-semibold bg-slate-50 border-b border-slate-200">You submitted</div>
              <div className="px-2 py-1.5 font-semibold bg-slate-50 border-b border-slate-200">FedEx suggestion</div>
              {[
                ["line1", "Street"],
                ["city",  "City"],
                ["state", "State"],
                ["postal_code", "Postal"],
                ["country", "Country"],
              ].map(([k, lbl]) => {
                const ok = matches[k] !== false;
                return (
                  <React.Fragment key={k}>
                    <div className="px-2 py-1.5 border-b border-slate-100 text-slate-600">{lbl}</div>
                    <div className="px-2 py-1.5 border-b border-slate-100 font-mono text-slate-800">{submitted[k] || "—"}</div>
                    <div
                      className={`px-2 py-1.5 border-b border-slate-100 font-mono ${ok ? "text-slate-800" : "bg-rose-100 text-rose-900 font-semibold"}`}
                      data-testid={`fedex-gate-suggestion-${k}`}
                    >
                      {suggestion[k] || "—"}{!ok && <span className="ml-1 text-[10px]">⚠ different</span>}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {v.raw_response && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowRaw((s) => !s)}
              data-testid="fedex-gate-toggle-raw"
              className="text-[11px] underline hover:no-underline text-slate-500"
            >
              {showRaw ? "Hide raw FedEx diagnostic" : "Show raw FedEx diagnostic (admin only)"}
            </button>
            {showRaw && (
              <pre
                className="mt-2 whitespace-pre-wrap break-all text-[10px] bg-slate-50 rounded p-2 border border-slate-200 max-h-72 overflow-auto"
                data-testid="fedex-gate-raw"
              >
                {JSON.stringify(v.raw_response, null, 2)}
              </pre>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {!hardBlock && (
            <Button
              onClick={onAcceptSuggestion}
              disabled={purchasing}
              data-testid="fedex-gate-accept"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {purchasing
                ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                : null}
              Accept FedEx suggestion and continue
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="fedex-gate-edit"
            disabled={purchasing}
          >
            {hardBlock ? "Edit address" : "Edit address instead"}
          </Button>
        </div>

        <div className="mt-3 text-[10px] text-slate-400 leading-relaxed">
          The full FedEx response is preserved server-side for diagnostics.
          Customers never see this view — only the admin label flow does.
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Address Validation panel (Provider Activation Sprint — Feb 2026)
// -----------------------------------------------------------------------------
// FedEx returns a *list of suggestions*, not a verified answer. For an
// input that doesn't cleanly resolve, the first suggestion can be
// totally unrelated to what was submitted (e.g. a Chilean fallback for
// an unmatched US address on a sandbox account that isn't US-scoped).
// We therefore render a Submitted-vs-Suggested comparison rather than
// a raw JSON blob, and highlight any fields where FedEx's candidate
// drifts from the input.
// =============================================================================
function AddressValidationPanel({ provider, onPipeAddress }) {
  const [draft, setDraft] = useState({
    line1: "1600 Amphitheatre Parkway",
    line2: "",
    city: "Mountain View",
    state: "CA",
    postal_code: "94043",
    country: "US",
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const supported = provider === "fedex";

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const draftComplete = !!(draft.line1 && draft.city && draft.state && draft.postal_code);
  const pipeThisAddress = (addr) => {
    if (!onPipeAddress) return;
    onPipeAddress({
      line1: addr.line1 || "",
      line2: addr.line2 || "",
      city: addr.city || "",
      state: addr.state || "",
      postal_code: addr.postal_code || "",
      country: (addr.country || "US").toUpperCase(),
    });
  };

  const run = async () => {
    setRunning(true);
    setResult(null);
    setShowRaw(false);
    try {
      const { data } = await api.post(
        `/shipping/providers/${provider}/validate-address`,
        draft,
      );
      setResult(data);
      if (!data.ok) {
        toast.message(data.messages?.[0] || data.detail || "Address could not be verified");
      } else {
        toast.success("Address verified");
      }
    } catch (e) { toast.error(e); }
    finally { setRunning(false); }
  };

  return (
    <Card data-testid="address-validation-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="w-4 h-4 text-indigo-600" />
          Address Validation
          {!supported && (
            <Badge variant="outline" className="text-[10px] text-slate-600 border-slate-300">
              FedEx only
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!supported ? (
          <div className="text-xs text-slate-500">
            Switch the Provider above to <b>FEDEX</b> to validate addresses against
            the FedEx Address Validation API. Other carriers don&apos;t expose this
            capability in the current sprint.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={draft.line1} onChange={(e) => set("line1", e.target.value)} placeholder="Street line 1" data-testid="addrv-line1" />
              <Input value={draft.line2} onChange={(e) => set("line2", e.target.value)} placeholder="Street line 2 (optional)" data-testid="addrv-line2" />
              <Input value={draft.city} onChange={(e) => set("city", e.target.value)} placeholder="City" data-testid="addrv-city" />
              <Input value={draft.state} onChange={(e) => set("state", e.target.value)} placeholder="State (e.g. CA)" maxLength={2} data-testid="addrv-state" />
              <Input value={draft.postal_code} onChange={(e) => set("postal_code", e.target.value)} placeholder="Postal code" data-testid="addrv-postal" />
              <Input value={draft.country} onChange={(e) => set("country", e.target.value.toUpperCase())} placeholder="Country (US)" maxLength={2} data-testid="addrv-country" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={run} disabled={running} data-testid="addrv-run">
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Validate Address
              </Button>
              {onPipeAddress && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => pipeThisAddress(draft)}
                  disabled={!draftComplete}
                  data-testid="addrv-use-as-destination"
                >
                  Use as test destination
                </Button>
              )}
            </div>

            {result && (
              <AddressValidationResult
                result={result}
                showRaw={showRaw}
                onToggleRaw={() => setShowRaw((v) => !v)}
                onAcceptSuggestion={(s) => setDraft((d) => ({
                  ...d,
                  line1:       s.line1 || d.line1,
                  line2:       s.line2 || d.line2,
                  city:        s.city || d.city,
                  state:       s.state || d.state,
                  postal_code: s.postal_code || d.postal_code,
                  country:     s.country || d.country,
                }))}
                onPipeSuggestion={onPipeAddress ? pipeThisAddress : null}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function AddressValidationResult({ result, showRaw, onToggleRaw, onAcceptSuggestion, onPipeSuggestion }) {
  // Tier the banner: green when FedEx verified AND country aligned,
  // amber when the address didn't fully resolve, red when the country
  // drifted (the symptom from the production bug report).
  const submitted = result.submitted || {};
  const suggestion = result.suggestion || null;
  const matches = result.suggestion_matches || {};
  const countryDrift = suggestion && matches.country === false;

  const tone = result.ok
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : countryDrift
      ? "bg-rose-50 border-rose-200 text-rose-900"
      : "bg-amber-50 border-amber-200 text-amber-900";

  return (
    <div data-testid="addrv-result" className={`p-3 rounded-lg border text-xs space-y-3 ${tone}`}>
      <div className="flex items-center gap-2 font-medium">
        {result.ok
          ? <CheckCircle2 className="w-4 h-4" />
          : <AlertCircle className="w-4 h-4" />}
        <span>
          {result.ok
            ? "Address verified by FedEx"
            : countryDrift
              ? "FedEx returned an unrelated suggestion"
              : "Address could not be fully verified"}
        </span>
        {typeof result.suggestion_count === "number" && result.suggestion_count > 1 && (
          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-white/60 border border-current">
            {result.suggestion_count} candidates
          </span>
        )}
      </div>

      {(result.messages || []).length > 0 && (
        <ul className="list-disc list-inside space-y-0.5" data-testid="addrv-messages">
          {result.messages.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}

      {suggestion && (
        <div className="bg-white/70 rounded-md border border-current/30 overflow-hidden">
          <div className="grid grid-cols-[110px_1fr_1fr] text-[11px]">
            <div className="px-2 py-1.5 font-semibold border-b border-current/20 bg-white/50">Field</div>
            <div className="px-2 py-1.5 font-semibold border-b border-current/20 bg-white/50">You submitted</div>
            <div className="px-2 py-1.5 font-semibold border-b border-current/20 bg-white/50">FedEx suggestion</div>
            {[
              ["line1", "Street"],
              ["city",  "City"],
              ["state", "State"],
              ["postal_code", "Postal"],
              ["country", "Country"],
            ].map(([k, lbl]) => {
              const ok = matches[k] !== false;
              return (
                <React.Fragment key={k}>
                  <div className="px-2 py-1.5 border-b border-current/10 text-slate-600">{lbl}</div>
                  <div className="px-2 py-1.5 border-b border-current/10 font-mono text-slate-800">{submitted[k] || "—"}</div>
                  <div
                    className={`px-2 py-1.5 border-b border-current/10 font-mono ${ok ? "text-slate-800" : "bg-rose-100 text-rose-900 font-semibold"}`}
                    data-testid={`addrv-suggestion-${k}`}
                    title={ok ? "" : "FedEx suggestion differs from what you submitted"}
                  >
                    {suggestion[k] || "—"}{!ok && <span className="ml-1 text-[10px]">⚠ different</span>}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          {result.classification && (
            <div className="px-2 py-1.5 text-[10px] text-slate-500 bg-white/40 border-t border-current/20">
              FedEx classification: <span className="font-medium">{result.classification}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {suggestion && !countryDrift && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAcceptSuggestion(suggestion)}
            data-testid="addrv-accept-suggestion"
          >
            Use FedEx suggestion
          </Button>
        )}
        {suggestion && !countryDrift && onPipeSuggestion && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onPipeSuggestion(suggestion)}
            data-testid="addrv-pipe-suggestion"
          >
            Use suggestion as test destination
          </Button>
        )}
        {result.raw_response && (
          <button
            type="button"
            onClick={onToggleRaw}
            data-testid="addrv-toggle-raw"
            className="text-[11px] underline hover:no-underline"
          >
            {showRaw ? "Hide raw FedEx response" : "Show raw FedEx response"}
          </button>
        )}
      </div>
      {showRaw && result.raw_response && (
        <pre
          className="mt-1 whitespace-pre-wrap break-all text-[10px] bg-white/70 rounded p-2 border border-current/20 max-h-72 overflow-auto"
          data-testid="addrv-raw"
        >
          {JSON.stringify(result.raw_response, null, 2)}
        </pre>
      )}
    </div>
  );
}

// =============================================================================
// Shipments tab — list + detail
// =============================================================================

function ShipmentsPanel({ refreshKey }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shipping/shipments?limit=50");
      setList(data.shipments || []);
    } catch (e) { toast.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  if (selectedId) {
    return (
      <ShipmentDetail
        shipmentId={selectedId}
        onBack={() => { setSelectedId(null); refresh(); }}
      />
    );
  }

  return (
    <Card data-testid="shipments-list-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <Package className="w-4 h-4 text-indigo-600" /> Recent Shipments
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={refresh} data-testid="refresh-shipments-btn">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>
        ) : list.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">
            No shipments yet — try purchasing a mock label from the
            <span className="font-medium text-slate-600"> Test Tools </span>tab.
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((s) => (
              <button
                key={s.id}
                data-testid={`shipment-row-${s.id}`}
                onClick={() => setSelectedId(s.id)}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-left"
              >
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    {s.tracking_number || "(no tracking)"}
                    <StatusPill status={s.status} />
                    <ShipmentModeBadge mode={s.mode} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {s.provider.toUpperCase()} · {s.service_code} ·
                    {" "}${s.rate_amount.toFixed(2)} {s.rate_currency} ·
                    {" "}{s.tracking_event_count} event(s)
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ShipmentDetail({ shipmentId, onBack }) {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [polling, setPolling] = useState(false);
  // Void support (added Feb 2026 as diagnostic polish — Test Tools stays diagnostics-only,
  // this button just exposes the existing POST /shipping/labels/{id}/void endpoint so
  // operators don't need to open DevTools to void a smoke-test label).
  const [voidingId, setVoidingId] = useState(null);      // label_id currently being voided
  const [voidConfirm, setVoidConfirm] = useState(null);  // label object pending confirmation

  const load = useCallback(async () => {
    try {
      const [d, ev] = await Promise.all([
        api.get(`/shipping/shipments/${shipmentId}`),
        api.get(`/shipping/shipments/${shipmentId}/events`),
      ]);
      setData(d.data);
      setEvents(ev.data?.events || []);
    } catch (e) { toast.error(e); }
  }, [shipmentId]);

  useEffect(() => { load(); }, [load]);

  const poll = async () => {
    setPolling(true);
    try {
      const { data: out } = await api.post(`/shipping/shipments/${shipmentId}/poll`);
      if (out.ok) toast.success(`${out.events_inserted} new event(s)`);
      else toast.message(out.detail || out.error || "Tracking poll skipped");
      await load();
    } catch (e) { toast.error(e); }
    finally { setPolling(false); }
  };

  const download = (labelId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/shipping/labels/${labelId}/download`;
    const token = localStorage.getItem("ren_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${labelId}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Label download failed"));
  };

  const voidLabel = async (labelId) => {
    setVoidingId(labelId);
    try {
      const { data: out } = await api.post(`/shipping/labels/${labelId}/void`);
      if (out?.ok) {
        toast.success("Label voided");
      } else {
        toast.error(out?.detail || out?.error || "Void failed");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Void failed");
    } finally {
      setVoidingId(null);
      setVoidConfirm(null);
    }
  };

  if (!data) return <div className="text-slate-500 py-8 text-center" data-testid="shipment-detail-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading…</div>;
  const s = data.shipment;
  const labels = data.labels || [];

  return (
    <div className="space-y-6" data-testid="shipment-detail-panel">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="shipment-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to list
        </Button>
        <Button variant="outline" size="sm" onClick={poll} disabled={polling} data-testid="shipment-poll-btn">
          {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Activity className="w-3.5 h-3.5 mr-1.5" />}
          Poll Tracking
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="w-4 h-4 text-indigo-600" /> Shipment
            <StatusPill status={s.status} />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-[11px] text-slate-500 uppercase">Tracking #</div>
            <div className="font-mono text-xs mt-0.5" data-testid="detail-tracking-number">
              {s.tracking_number || "—"}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500 uppercase">Provider</div>
            <div className="mt-0.5">{s.provider.toUpperCase()} · {s.service_code}</div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500 uppercase">Rate</div>
            <div className="mt-0.5">${s.rate_amount.toFixed(2)} {s.rate_currency}</div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500 uppercase">Order</div>
            <div className="mt-0.5">{s.order_id || "—"}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[11px] text-slate-500 uppercase">From</div>
            <div className="mt-0.5 text-xs">
              {s.from_address?.line1}, {s.from_address?.city} {s.from_address?.state} {s.from_address?.postal_code}
            </div>
          </div>
          <div className="col-span-2">
            <div className="text-[11px] text-slate-500 uppercase">To</div>
            <div className="mt-0.5 text-xs">
              {s.to_address?.line1}, {s.to_address?.city} {s.to_address?.state} {s.to_address?.postal_code}
            </div>
          </div>
        </CardContent>
      </Card>

      {labels.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Download className="w-4 h-4 text-indigo-600" /> Labels
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {labels.map((l) => (
              <div key={l.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-white" data-testid={`label-row-${l.id}`}>
                <div>
                  <div className="text-sm font-mono">{l.tracking_number}</div>
                  <div className="text-[11px] text-slate-500">
                    {l.provider.toUpperCase()} · {l.label_format.toUpperCase()} · {l.content_type}
                    {l.voided_at && <span className="ml-2 text-rose-600">voided</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => download(l.id)}
                    data-testid={`download-label-${l.id}`}
                    disabled={!!l.voided_at}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Download PDF
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setVoidConfirm(l)}
                    data-testid={`void-label-${l.id}`}
                    disabled={!!l.voided_at || voidingId === l.id}
                    className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                  >
                    {voidingId === l.id
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <Ban className="w-3.5 h-3.5 mr-1.5" />}
                    Void
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="w-4 h-4 text-indigo-600" /> Tracking Events
            <Badge variant="outline" className="text-[10px] ml-1">{events.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center py-6 text-slate-400 text-sm">No tracking events yet.</div>
          ) : (
            <ol className="relative border-l border-slate-200 ml-2" data-testid="events-list">
              {events.map((ev, idx) => (
                <li key={ev.id} className="mb-4 ml-4" data-testid={`event-row-${idx}`}>
                  <div className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-indigo-500 border-2 border-white" />
                  <div className="flex items-center gap-2 text-sm">
                    <StatusPill status={ev.status} />
                    <span className="text-[11px] text-slate-500">{ev.source}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {ev.occurred_at} {ev.location ? `· ${ev.location}` : ""}
                  </div>
                  {ev.status_detail && (
                    <div className="text-[11px] text-slate-400 mt-0.5">{ev.status_detail}</div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* Void confirmation dialog (diagnostic polish — Test Tools) */}
      <Dialog open={!!voidConfirm} onOpenChange={(open) => !open && setVoidConfirm(null)}>
        <DialogContent data-testid="void-label-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-4 h-4 text-rose-600" /> Void this label?
            </DialogTitle>
            <DialogDescription>
              This will cancel the carrier label and record a void event in the shipment history.
              This action cannot be undone. Depending on the carrier, this may or may not fully
              refund the label cost.
            </DialogDescription>
          </DialogHeader>
          {voidConfirm && (
            <div className="text-sm space-y-1 py-2">
              <div><span className="text-slate-500">Tracking #:</span> <span className="font-mono">{voidConfirm.tracking_number}</span></div>
              <div><span className="text-slate-500">Provider:</span> {voidConfirm.provider?.toUpperCase()}</div>
              <div><span className="text-slate-500">Label ID:</span> <span className="font-mono text-xs">{voidConfirm.id}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setVoidConfirm(null)}
              disabled={!!voidingId}
              data-testid="void-label-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => voidConfirm && voidLabel(voidConfirm.id)}
              disabled={!!voidingId}
              data-testid="void-label-confirm-btn"
            >
              {voidingId
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Voiding…</>
                : <><Ban className="w-3.5 h-3.5 mr-1.5" />Void Label</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WebhooksPanel() {
  const webhookBase = `${process.env.REACT_APP_BACKEND_URL}/api/shipping/webhooks`;

  return (
    <Card data-testid="webhooks-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Webhook className="w-4 h-4 text-indigo-600" /> Carrier Webhook Endpoints
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          Each carrier delivers tracking events to a dedicated public endpoint.
          The endpoint authenticates via <span className="font-mono text-xs">X-Webhook-Signature</span>
          {" "}(HMAC-SHA256 over the raw body, using your tenant&apos;s webhook secret).
        </div>
        <div className="space-y-2">
          {["mock", "usps", "fedex", "ups"].map((p) => (
            <div key={p} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
              <div className="text-[11px] text-slate-500 uppercase">{p}</div>
              <div className="font-mono text-xs mt-0.5" data-testid={`webhook-url-${p}`}>
                POST {webhookBase}/{p}
              </div>
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            The webhook secret is stored encrypted at rest. Configure it via the
            credentials API — never paste the secret into the UI.
            See the platform docs for the per-carrier signature scheme.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Top-level page
// =============================================================================

export default function ShippingAdminPage() {
  const [config, setConfig] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipmentsRefreshKey, setShipmentsRefreshKey] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/shipping/config"),
        api.get("/shipping/providers"),
      ]);
      setConfig(c.data?.config);
      setProviders(p.data?.providers || []);
    } catch (e) { toast.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl" data-testid="shipping-admin-page">
        <PageHeader />
        {loading || !config ? (
          <div className="text-center py-16 text-slate-500" data-testid="shipping-loading">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading shipping configuration…
          </div>
        ) : (
          <Tabs defaultValue="settings" className="w-full">
            <TabsList className="mb-4" data-testid="shipping-tabs">
              <TabsTrigger value="settings" data-testid="tab-settings">
                <Settings className="w-3.5 h-3.5 mr-1.5" /> Settings
              </TabsTrigger>
              <TabsTrigger value="tools" data-testid="tab-tools">
                <FlaskConical className="w-3.5 h-3.5 mr-1.5" /> Test Tools
              </TabsTrigger>
              <TabsTrigger value="shipments" data-testid="tab-shipments">
                <Package className="w-3.5 h-3.5 mr-1.5" /> Shipments
              </TabsTrigger>
              <TabsTrigger value="webhooks" data-testid="tab-webhooks">
                <Webhook className="w-3.5 h-3.5 mr-1.5" /> Webhooks
              </TabsTrigger>
            </TabsList>
            <TabsContent value="settings">
              <SettingsPanel config={config} providers={providers} onChanged={refresh} />
            </TabsContent>
            <TabsContent value="tools">
              <TestToolsPanel
                config={config}
                onShipmentCreated={() => setShipmentsRefreshKey((k) => k + 1)}
              />
            </TabsContent>
            <TabsContent value="shipments">
              <ShipmentsPanel refreshKey={shipmentsRefreshKey} />
            </TabsContent>
            <TabsContent value="webhooks">
              <WebhooksPanel />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
